import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CalendarDays, GripVertical, Wheat } from "lucide-react";
import { formatDate } from "@/lib/format";

const MONTHS_FR = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
const MONTHS_EN = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function monthKey(iso) {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function Timeline() {
  const { t, lang } = useI18n();
  const [events, setEvents] = useState([]);
  const [dragging, setDragging] = useState(null);
  const [dragOverMonth, setDragOverMonth] = useState(null);
  const months = lang === "fr" ? MONTHS_FR : MONTHS_EN;

  async function load() {
    const r = await api.get("/timeline");
    setEvents(r.data);
  }
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const e of events) {
      const k = monthKey(e.expected_harvest_date);
      if (!g[k]) g[k] = [];
      g[k].push(e);
    }
    return g;
  }, [events]);

  const monthKeys = useMemo(() => {
    const now = new Date();
    const keys = new Set(Object.keys(grouped));
    for (let i = 0; i < 4; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      keys.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    }
    return Array.from(keys).sort();
  }, [grouped]);

  async function onDrop(month) {
    if (!dragging) return;
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1, 15, 12, 0, 0);
    try {
      await api.post(`/plantings/${dragging.id}/reschedule`, {
        expected_harvest_date: d.toISOString(),
      });
      toast.success(t("updated"));
      load();
    } catch {
      toast.error(t("error"));
    }
    setDragging(null); setDragOverMonth(null);
  }

  const STATUS_COLORS = { harvested: "bg-muted text-muted-foreground", upcoming: "bg-primary/10 text-primary", late: "bg-destructive/10 text-destructive" };

  return (
    <div className="space-y-6" data-testid="timeline-page">
      <div>
        <div className="label-eyebrow mb-2">{t("parcelles")}</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">{t("harvest_timeline")}</h1>
        <p className="text-muted-foreground mt-1">{t("drag_hint")}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {monthKeys.map((mk) => {
          const [y, m] = mk.split("-").map(Number);
          const items = grouped[mk] || [];
          return (
            <Card
              key={mk}
              className={`border-border min-h-[180px] transition-colors ${dragOverMonth === mk ? "ring-2 ring-primary bg-primary/5" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setDragOverMonth(mk); }}
              onDragLeave={() => setDragOverMonth(null)}
              onDrop={(e) => { e.preventDefault(); onDrop(mk); }}
              data-testid={`timeline-month-${mk}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-semibold flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-primary" />
                    {months[m - 1]} {y}
                  </div>
                  <Badge variant="secondary">{items.length}</Badge>
                </div>
                <div className="space-y-2">
                  {items.map((ev) => (
                    <div
                      key={ev.id}
                      draggable={ev.status !== "harvested"}
                      onDragStart={() => setDragging(ev)}
                      onDragEnd={() => { setDragging(null); setDragOverMonth(null); }}
                      className={`rounded-lg border border-border p-3 cursor-grab active:cursor-grabbing ${STATUS_COLORS[ev.status]} ${dragging?.id === ev.id ? "opacity-40" : ""}`}
                      data-testid={`timeline-event-${ev.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="font-medium text-sm flex items-center gap-1.5">
                            <Wheat className="w-3.5 h-3.5" /> {ev.culture_name}
                          </div>
                          <div className="text-xs opacity-75 mt-0.5">{ev.parcelle_name} · {ev.area_ha} ha</div>
                          <div className="text-xs opacity-60 mt-0.5">{formatDate(ev.expected_harvest_date, lang)}</div>
                        </div>
                        {ev.status !== "harvested" && <GripVertical className="w-4 h-4 opacity-40" />}
                      </div>
                    </div>
                  ))}
                  {items.length === 0 && <div className="text-xs text-muted-foreground py-4 text-center border border-dashed border-border rounded-lg">{t("drop_here")}</div>}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
